import { useState, useContext, useEffect, useRef } from "react"
import { useParams, useLocation } from "react-router-dom"
import { useQuery } from "@apollo/client"
import _ from "lodash"
import AppBreadcrumb from "../../components/AppBreadcrumb/AppBreadcrumb"
import styles from "../../styles/customDataForms/CustomDataForms.module.css"
import { Accordion, AccordionTab } from "primereact/accordion"
import { SelectButton } from "primereact/selectbutton"
import { Checkbox } from "primereact/checkbox"

// Context
import { UserContext } from "../../context/userContext"

// Queries
import { GET_TEMPLATE_VIEW_MODE } from "./queries"
import { GET_MY_ORGANIZATIONS } from "../Preferences/Participant/queries.js"

// Helpers
import FieldValuePerType from "./FieldValuePerType"
import LoaderFullPage from "../../components/Loaders"
import { Button } from "primereact/button"
import PrevBreadLink from "../../components/AppBreadcrumb/PrevBreadLink"
import { LogError } from "../../helpers/logger"
import { transformPayload } from "../../helpers/dateNormalization"
import { classNames } from "primereact/utils"

export default function FormViewMode() {
  const [formValues, setFormValues] = useState({})
  const [titlePage, setTitle] = useState({})
  const userCtx = useContext(UserContext)
  const { idOrganization, idTemplate, idStamp = null } = useParams()
  const { state } = useLocation()
  const staffId = state?.staffId
  const TYPE = "system"
  const [activeIndex, setActiveIndex] = useState([])
  const location = useLocation()
  const topRef = useRef(null)

  const { loading: orgLoading, data: orgData } = useQuery(
    GET_MY_ORGANIZATIONS,
    {
      variables: { participant_id: userCtx?.participant_id },
      fetchPolicy: "network-only",
      skip: !userCtx?.participant_id,
      onError: (error) => {
        LogError("Error fetching organization data:", error)
      },
    },
  )

  const { loading: templateLoading, data: templateData } = useQuery(
    GET_TEMPLATE_VIEW_MODE,
    {
      variables: {
        staff_id: staffId ? staffId : "0",
        organization_id: organization()?.id,
        enterprise_id: organization()?.enterprise_id,
        idTemplate,
        type: TYPE,
        participant_id: userCtx?.participant_id,
        idStamp,
      },
      skip:
        !organization()?.id ||
        !userCtx?.participant_id ||
        !idTemplate ||
        !idStamp, // Skip if any required variable is missing
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        const parsedFormValues = JSON.parse(data.template.values)
        const filterEmptyValues = _.reduce(
          parsedFormValues,
          (acc, value, key) => {
            if (_.isEmpty(value)) return acc
            return {
              ...acc,
              [key]: value,
            }
          },
          {},
        )

        // For each block, transform the values if needed based on the block definition
        const blocksByName = _.keyBy(data.template.blocks, "block_name")
        const normalizedFormValues = {}
        Object.entries(filterEmptyValues).forEach(
          ([blockName, blockValues]) => {
            // Get block definition
            const blockDef = blocksByName[blockName]

            // Transform values if needed (e.g. date normalization), otherwise keep original values
            normalizedFormValues[blockName] =
              transformPayload(blockValues, [blockDef]) || blockValues
          },
        )

        setTitle(data.template.template_title)
        setFormValues(normalizedFormValues)
      },
      onError: (error) => {
        LogError("Error fetching template data", error)
      },
    },
  )

  // Combine loading states
  const loading = orgLoading || templateLoading

  // Filter on current organization
  function organization() {
    return orgData?.organizations?.find((o) => o.id === idOrganization) || {}
  }

  const orderedFields = [
    "participant_name_preferred",
    "participant_pronouns",
    "participant_email_secondary",
    "participant_phone_primary",
    "participant_preferred_contact",
    "participant_phone_primary_preference",
  ]

  const pairedFields = {
    participant_name_preferred: "participant_pronouns",
    participant_pronouns: "participant_name_preferred",
    participant_email_secondary: "participant_phone_primary",
    participant_phone_primary: "participant_email_secondary",
  }

  const renderField = (field_name, blockValues) => {
    switch (field_name) {
      case "participant_name_preferred":
        return (
          <>
            <label className={styles.viewDataBlockItemLabel}>
              Preferred name
            </label>
            <div className={styles.viewDataBlockItemValue}>
              {blockValues[field_name]}
            </div>
          </>
        )
      case "participant_pronouns":
        return (
          <>
            <label className={styles.viewDataBlockItemLabel}>Pronouns</label>
            <div className={styles.viewDataBlockItemValue}>
              {blockValues[field_name]}
            </div>
          </>
        )
      case "participant_email_secondary":
        return (
          <>
            <label className={styles.viewDataBlockItemLabel}>
              Secondary email address
            </label>
            <div className={styles.viewDataBlockItemValue}>
              {blockValues[field_name]}
            </div>
          </>
        )
      case "participant_phone_primary":
        return (
          <>
            <label className={styles.viewDataBlockItemLabel}>
              Phone Number
            </label>
            <div className={styles.viewDataBlockItemValue}>
              {blockValues[field_name]}
            </div>
          </>
        )
      case "participant_preferred_contact":
        return (
          <>
            <label className={styles.viewDataBlockItemLabel}>
              Preferred method to contact participant
            </label>
            <div className={styles.viewDataBlockItemValue}>
              {blockValues[field_name]}
            </div>
          </>
        )
      case "participant_phone_primary_preference":
        return (
          <div>
            <label className={styles.viewDataBlockItemLabel}>
              Preferences for primary phone number
            </label>
            <div className="flex align-items-center">
              <Checkbox
                inputId="ingredient1"
                name="pizza"
                value="Cheese"
                className={styles.checkboxFormViewMode}
                onChange={() => {}}
                checked={
                  blockValues[field_name] &&
                  blockValues[field_name].includes("Okay to contact via phone")
                }
              />
              <label
                className={`${styles.labelCheckbox} ml-2`}
                htmlFor="ingredient1"
              >
                Okay to contact via phone
              </label>
            </div>
            <div className="flex align-items-center">
              <Checkbox
                inputId="ingredient2"
                name="pizza"
                value="Cheese"
                className={styles.checkboxFormViewMode}
                onChange={() => {}}
                checked={
                  blockValues[field_name] &&
                  blockValues[field_name].includes("Okay to leave a voicemail")
                }
              />
              <label
                className={`${styles.labelCheckbox} ml-2`}
                htmlFor="ingredient2"
              >
                Okay to leave a voicemail
              </label>
            </div>
            <div className="flex align-items-center">
              <Checkbox
                inputId="ingredient3"
                name="pizza"
                value="Cheese"
                className={styles.checkboxFormViewMode}
                onChange={() => {}}
                checked={
                  blockValues[field_name] &&
                  blockValues[field_name].includes("Okay to text")
                }
              />
              <label
                className={`${styles.labelCheckbox} ml-2`}
                htmlFor="ingredient3"
              >
                Okay to text
              </label>
            </div>
          </div>
        )
      default:
        return null
    }
  }

  useEffect(() => {
    if (templateData) {
      setActiveIndex([...templateData.template.blocks.keys()])
    }
  }, [templateData])

  const handleFieldsPerBlock = (fields, block_name) => {
    const blockValues = formValues[block_name]

    // If the block is a "Form Information", inject organization name field
    if (block_name === "form" || block_name === "session") {
      // Add to fields
      fields = [
        {
          description: "Organization Name",
          field_description: "Organization Name",
          field_label: "Organization Name",
          field_name: "organization_name",
          field_type: "text",
          field_options: null,
          __typename: "TypeField",
        },
        ...fields,
      ]

      // Add to blockValues
      blockValues["organization_name"] =
        organization()?.name || "Unknown Organization"
    }

    // Special handling for contact information block
    if (block_name === "participant__contact_information") {
      const processedFields = new Set()
      const renderedFields = []

      // Process fields in the predefined order
      for (let i = 0; i < orderedFields.length; i++) {
        const fieldName = orderedFields[i]

        // Check if the field exists in this block
        const fieldExists = fields.some((f) => f.field_name === fieldName)
        if (!fieldExists) continue

        // Skip already processed fields
        if (processedFields.has(fieldName)) continue

        // Check if this field should be paired with another field
        const pairedField = pairedFields[fieldName]
        const pairedFieldExists =
          pairedField && fields.some((f) => f.field_name === pairedField)

        if (
          pairedField &&
          pairedFieldExists &&
          !processedFields.has(pairedField)
        ) {
          // Both fields go in the same row
          const fieldContent1 = renderField(fieldName, blockValues)
          const fieldContent2 = renderField(pairedField, blockValues)

          if (fieldContent1 || fieldContent2) {
            renderedFields.push(
              <div key={fieldName} className={styles.viewDataBlockItem}>
                <div
                  key={`pair-${fieldName}-${pairedField}`}
                  className={styles.fieldRow}
                >
                  {fieldContent1 && (
                    <div
                      id={fieldName}
                      key={fieldName}
                      className={styles.fieldColumn}
                    >
                      {fieldContent1}
                    </div>
                  )}
                  {fieldContent2 && (
                    <div
                      id={pairedField}
                      key={pairedField}
                      className={styles.fieldColumn}
                    >
                      {fieldContent2}
                    </div>
                  )}
                </div>
              </div>,
            )
          }

          // Mark both fields as processed
          processedFields.add(fieldName)
          processedFields.add(pairedField)
        } else if (!processedFields.has(fieldName)) {
          // This field goes alone in a row
          const fieldContent = renderField(fieldName, blockValues)

          if (fieldContent) {
            renderedFields.push(
              <div
                id={fieldName}
                key={fieldName}
                className={styles.viewDataBlockItem}
              >
                {fieldContent}
              </div>,
            )
          }

          // Mark field as processed
          processedFields.add(fieldName)
        }
      }

      // Only return the fields we've processed in our ordered way
      // No processing of remaining fields that weren't in orderedFields
      return renderedFields
    }

    // Handle consent block
    else if (block_name === "participant__consent") {
      return _.map(
        fields,
        ({
          field_label,
          field_name,
          field_type,
          field_options,
          field_description,
        }) => {
          // Skip hidden fields
          const skipField = field_type === "hidden" || field_type === "sys"
          if (skipField) return null

          const handleLabel = () => {
            if (field_type === "file") {
              return "File"
            }
            return field_label
          }

          // Return consent field with specific styling
          return (
            <div
              id={field_name}
              key={field_name}
              className={styles.contentBlockConsent}
            >
              <div>
                <label className={styles.viewDataBlockItemLabelConsent}>
                  {handleLabel()}
                </label>
                <div className={styles.viewDataBlockItemValueConsent}>
                  {field_description}
                </div>
              </div>
              <div>
                <SelectButton
                  className={styles.selectButton}
                  value={blockValues[field_name]}
                  disabled={true}
                  options={[
                    { label: "Yes", value: "Yes" },
                    { label: "No", value: "No" },
                  ]}
                  tabIndex={0}
                />
              </div>
            </div>
          )
        },
      ).filter(Boolean) // Remove null values
    }

    // Handle all other blocks with standard rendering
    else {
      return _.map(
        fields,
        ({
          field_label,
          field_name,
          field_type,
          field_type_subtype,
          field_options,
          field_description,
        }) => {
          const skipField = field_type === "hidden" || field_type === "sys"
          if (skipField) return null

          const fieldValuePerTypeArg = {
            blockValues,
            fieldName: field_name,
            fieldType: field_type,
            fieldTypeSubtype: field_type_subtype,
            fieldOptions: field_options,
            description: field_description,
          }

          const handleLabel = () => {
            if (field_type === "file") {
              return "File"
            }
            return field_label
          }

          if (field_label) {
            return (
              <div
                id={field_name}
                key={field_name}
                className={styles.viewDataBlockItem}
              >
                <label className={styles.viewDataBlockItemLabel}>
                  {handleLabel()}
                </label>
                <div className={styles.viewDataBlockItemValue}>
                  <FieldValuePerType
                    fieldValuePerTypeArg={fieldValuePerTypeArg}
                  />
                </div>
              </div>
            )
          }

          return null
        },
      ).filter(Boolean) // Remove null values
    }
  }

  const getTitle = () => {
    if (!_.isNull(titlePage)) return titlePage.toString()
  }

  const handleBlocks = () => {
    const { blocks } = templateData.template
    const blocksOrderedByIndex = _.orderBy(blocks, ["index"])
    return _.map(
      blocksOrderedByIndex,
      ({ block_title, block_name, fields }) => {
        if (!_.has(formValues, block_name)) return null

        const blockValues = formValues[block_name]
        const hasContent = fields.some((field) => {
          const fieldName = field.field_name || field.name
          return blockValues[fieldName] && blockValues[fieldName] !== ""
        })

        if (!hasContent) return null

        const cleanTitle = block_title.replace(/participant/gi, "").trim()
        return (
          <AccordionTab key={block_name} header={cleanTitle} id={block_name}>
            <div className={styles.viewDataBlockFormView}>
              {handleFieldsPerBlock(fields, block_name)}
            </div>
          </AccordionTab>
        )
      },
    )
  }

  const scrollToTop = () => {
    topRef?.current?.scrollIntoView({ behavior: "smooth" })
  }

  if (loading) return <LoaderFullPage />

  return (
    <div className="main-content">
      <AppBreadcrumb
        items={[
          {
            label: "Custom Data Forms",
            template: (
              <PrevBreadLink
                previousRoute={location?.state?.previousRoute}
                previousTitle={location?.state?.previousTitle}
              />
            ),
          },
          { label: "View" },
        ]}
      />
      <div ref={topRef} className="w-full">
        <h1 className={classNames(styles.title, "mb-4")}>
          {location?.state?.previousTitle}
        </h1>
        <hr className="line" />
        <h2 className={`${styles.subTitle} mb-4 mt-5`}>{getTitle()}</h2>
        <Accordion
          className={styles.customFormAccordion}
          activeIndex={activeIndex}
          multiple
          onTabChange={(e) => setActiveIndex(e.index)}
        >
          {handleBlocks()}
        </Accordion>
        {(activeIndex.length > 1 ||
          (activeIndex[0] !== 0 && activeIndex.length > 0)) && (
          <div className={styles.pageFooter}>
            <Button
              label="Back to top"
              className={styles.backToTopBtn}
              onClick={scrollToTop}
            />
          </div>
        )}
      </div>
    </div>
  )
}
